import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, BookOpen, FlaskConical, AlertTriangle, Star, ExternalLink } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { fetchPeptides } from "../api";
import { usePageSEO } from "../hooks/usePageSEO";
import { getTierForSlug } from "../data/peptideTiers";
import JsonLd from "../components/JsonLd";
import { PEPTIDE_SEO, SLUG_MAP, RESEARCH_KEY_LENGTH } from "../data/peptideContent";
import { PeptideHero } from "../components/PeptideHero";
import { PeptideAcquireSection } from "../components/PeptideAcquireSection";
import { PeptideFAQ } from "../components/PeptideFAQ";
import { AFFILIATE_URLS, trackAffiliateClick } from "../config/affiliate";

const SITE_BASE = "https://www.peptidesformedspas.org";
const SITE_IMAGE = `${SITE_BASE}/og-image.jpg`;

const PeptideDetailPage = () => {
  const { slug } = useParams();
  const seo = PEPTIDE_SEO[slug];
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  usePageSEO(
    seo ? seo.title : "Peptide Details | PeptidesForMedSpas",
    seo ? seo.description : undefined
  );

  // Evidence-tier classification for the hero badge + JSON-LD enrichment.
  const tierInfo = getTierForSlug(slug);

  const loadData = useCallback(async () => {
    try {
      const data = await fetchPeptides();
      setAllProducts(data);
      if (seo) {
        const found = data.find(p => p.id === seo.dbId);
        setProduct(found || null);
      }
    } catch (e) {
      console.error(`[PeptideDetailPage] Failed to load peptides for '${slug}':`, e);
    } finally {
      setLoading(false);
    }
  }, [seo, slug]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!seo) {
    return (
      <div className="min-h-screen bg-[#fffaf5]">
        <Header />
        <div className="flex flex-col items-center justify-center py-40">
          <h1 className="text-2xl text-[#3a2a26] mb-4">Peptide Not Found</h1>
          <Link to="/shop" className="text-[#b89a6d] hover:text-[#a08560]">Browse All Products</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedProducts = allProducts.filter(p => SLUG_MAP[p.id] !== slug).slice(0, 3);

  // Per-product JSON-LD: Product + Offer + BreadcrumbList. `image` field is
  // required for Google merchant-listing validation (see PRD.md 2026-05-26 fix).
  const productUrl = `${SITE_BASE}/peptides/${slug}`;
  const productJsonLd = product ? [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "@id": `${productUrl}#product`,
      name: product.name,
      description: seo.overview,
      image: SITE_IMAGE,
      url: productUrl,
      sku: `PFM-${product.id}`,
      category: product.category,
      brand: { "@type": "Brand", name: "PeptidesForMedSpas" },
      additionalProperty: [
        { "@type": "PropertyValue", name: "Regulatory Tier", value: `Tier ${tierInfo.tier} — ${tierInfo.label}` },
        { "@type": "PropertyValue", name: "Intended Use", value: "Laboratory research use only. Not for human or veterinary consumption. Not FDA-approved." },
      ],
      offers: {
        "@type": "Offer",
        url: product.buyLink || productUrl,
        priceCurrency: "USD",
        price: (() => {
          const m = String(product.price || "").match(/\d+(?:\.\d+)?/);
          return m ? m[0] : "0";
        })(),
        availability: "https://schema.org/InStock",
        itemCondition: "https://schema.org/NewCondition",
        priceValidUntil: "2026-12-31",
        seller: { "@type": "Organization", name: "Paramount Peptides (affiliate)" },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${productUrl}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_BASE}/` },
        { "@type": "ListItem", position: 2, name: "Shop", item: `${SITE_BASE}/shop` },
        { "@type": "ListItem", position: 3, name: product.name, item: productUrl },
      ],
    },
  ] : null;

  return (
    <div className="min-h-screen bg-[#fffaf5]">
      <Header />
      {productJsonLd && <JsonLd id={`peptide-jsonld-${slug}`} data={productJsonLd} />}

      {/* Breadcrumb */}
      <div className="pt-24 pb-2 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-[#a09186]" data-testid="peptide-breadcrumb">
            <Link to="/" className="hover:text-[#b89a6d]">Home</Link>
            <span>/</span>
            <Link to="/shop" className="hover:text-[#b89a6d]">Shop</Link>
            <span>/</span>
            <span className="text-[#b89a6d]">{product?.name || slug}</span>
          </nav>
        </div>
      </div>

      <PeptideHero slug={slug} seo={seo} product={product} tierInfo={tierInfo} />

      {/* Product Specs + Research Summary */}
      {product && !loading && (
        <section className="py-12 bg-[#fffaf5]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white border border-[#f0d8d4] rounded-xl p-6">
                <h2 className="text-xl font-bold text-[#3a2a26] mb-4 flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-[#b89a6d]" /> Product Specifications
                </h2>
                <ul className="space-y-3">
                  {product.benefits.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-[#5a4a40] text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#b89a6d]" />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-4 pt-4 border-t border-[#f0d8d4]">
                  <div className="flex items-center gap-2 text-[#7a6b62] text-sm">
                    <Star className="w-4 h-4 text-[#b89a6d] fill-[#b89a6d]" />
                    {product.rating} / 5.0 rating
                  </div>
                  <p className="text-[#a09186] text-xs mt-2">{product.researchNotes}</p>
                </div>
              </div>
              <div className="bg-white border border-[#f0d8d4] rounded-xl p-6">
                <h2 className="text-xl font-bold text-[#3a2a26] mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-[#b89a6d]" /> Research Summary
                </h2>
                <ul className="space-y-3">
                  {seo.research.map((r) => (
                    <li key={r.slice(0, RESEARCH_KEY_LENGTH)} className="text-[#5a4a40] text-sm leading-relaxed pl-4 border-l-2 border-[#e8c5b8]">
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      <PeptideAcquireSection product={product} slug={slug} />

      {/* Cosmetic-grade skincare partner callout — GHK-Cu is the natural fit */}
      {slug === "ghk-cu" && (
        <section className="py-8 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <a
              href={AFFILIATE_URLS.alohi}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              onClick={() => trackAffiliateClick({
                partner: "alohi",
                product: "Alohi GHK Facemask",
                destination: AFFILIATE_URLS.alohi,
                sourcePage: `/peptides/${slug}`,
              })}
              className="block bg-gradient-to-br from-[#fdf2f4] to-[#fffaf5] border-2 border-[#e8c5b8] hover:border-[#d4a5ad] rounded-2xl p-6 transition-colors group"
              data-testid="alohi-skincare-callout"
            >
              <p className="text-xs font-bold text-[#b89a6d] uppercase tracking-wider mb-2">
                Cosmetic skincare partner
              </p>
              <h3 className="text-lg md:text-xl font-bold text-[#3a2a26] mb-2 group-hover:text-[#b89a6d] transition-colors">
                Some incredible skin care we work with — the GHK Facemask is one of the best we have seen
              </h3>
              <p className="text-sm text-[#5a4a40] leading-relaxed mb-2">
                <strong>Alohi Skin &amp; Wellness</strong> makes a cosmetic-grade GHK-Cu facemask we recommend to anyone reading this page and looking for an at-home topical complement. Retail cosmetic product — not a drug, not a compounded prescription, not a research chemical.
              </p>
              <span className="text-sm text-[#b89a6d] font-semibold inline-flex items-center gap-1">
                Visit alohiskinandwellness.com <ExternalLink className="w-4 h-4" />
              </span>
            </a>
          </div>
        </section>
      )}

      <PeptideFAQ faqs={seo.faqs} />

      {/* Related Blog Posts */}
      <section className="py-12 bg-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#3a2a26] mb-8">Related Educational Articles</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {seo.relatedBlogs.map((blogSlug) => (
              <Link
                key={blogSlug}
                to={`/blog/${blogSlug}`}
                className="bg-white border border-[#f0d8d4] rounded-xl p-5 hover:border-[#d4a5ad] transition-all group"
              >
                <BookOpen className="w-5 h-5 text-[#b89a6d] mb-3" />
                <span className="text-[#3a2a26] font-medium group-hover:text-[#b89a6d] transition-colors text-sm">
                  {blogSlug.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-[#3a2a26] mb-8">Other Products</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedProducts.map((p) => (
                <Link
                  key={p.id}
                  to={`/peptides/${SLUG_MAP[p.id]}`}
                  className="bg-[#fffaf5] border border-[#f0d8d4] rounded-xl p-5 hover:border-[#d4a5ad] transition-all group"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#3a2a26] font-bold group-hover:text-[#b89a6d] transition-colors">{p.name}</span>
                    <span className="text-[#b89a6d] text-sm">{p.price}</span>
                  </div>
                  <p className="text-[#7a6b62] text-xs line-clamp-2">{p.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Loading fallback */}
      {loading && (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 text-[#b89a6d] animate-spin" />
        </div>
      )}

      {/* Disclaimer */}
      <section className="py-8 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#b89a6d] flex-shrink-0 mt-0.5" />
              <div className="text-[#7a6b62] text-sm">
                <p className="font-medium text-[#b89a6d] mb-2">Important Disclaimer</p>
                <p>This page is for educational and informational purposes only. This product is a research chemical sold for laboratory use only.
                Not intended for human consumption. Not intended to diagnose, treat, cure, or prevent any disease.
                Always consult a qualified healthcare provider. These statements have not been evaluated by the FDA.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PeptideDetailPage;
